import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useParams} from 'react-router';
import {
    Box,
    Chip,
    CircularProgress,
    InputAdornment,
    Paper,
    Skeleton,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Tooltip,
    Typography,
    IconButton,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import {getReportMentionCounts, getReportMentions} from '../../../utils/api/adminEntities';

// ── Constants ────────────────────────────────────────────────────────────────

const STATUS_TABS = [
    {key: 'all',      label: 'All Linked', countKey: 'all'},
    {key: 'wikidata', label: 'Wikidata',   countKey: 'wikidata'},
    {key: 'did',      label: 'DID',        countKey: 'did'},
    {key: 'unlinked', label: 'Unlinked',   countKey: 'unlinked'},
];

const PAGE_SIZE = 50;

// ── Status chip ──────────────────────────────────────────────────────────────

function StatusChip({status}) {
    const props = {
        all:      {label: 'linked',   color: 'default', variant: 'outlined'},
        wikidata: {label: 'wikidata', color: 'success', variant: 'filled'},
        did:      {label: 'did',      color: 'secondary', variant: 'filled'},
        unlinked: {label: 'unlinked', color: 'warning', variant: 'outlined'},
    }[status] || {label: status, color: 'default', variant: 'outlined'};

    return (
        <Chip
            label={props.label}
            color={props.color}
            variant={props.variant}
            size="small"
            sx={{fontSize: 10, height: 20, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase'}}
        />
    );
}

// ── ID chip ──────────────────────────────────────────────────────────────────

function IdChip({wikidataId}) {
    if (!wikidataId) return <Typography variant="caption" color="text.disabled">—</Typography>;
    const isQ = wikidataId.startsWith('Q');
    return (
        <Chip
            label={wikidataId}
            size="small"
            color={isQ ? 'success' : 'secondary'}
            variant="outlined"
            sx={{fontFamily: 'monospace', fontSize: 11, fontWeight: 700, height: 22}}
        />
    );
}

// ── Link source chip ─────────────────────────────────────────────────────────
// Distinguishes exactly who/what created the link:
//   auto               — ner-service pipeline: GLiNER (mention detection) + ReFinED (entity
//                         linking), no human involved. Tooltip shows the actual confidence scores.
//   manual-annotation  — a person directly linked this mention. Tooltip shows their name.
//   manual-propagated  — auto-applied elsewhere in the doc from a manual link on the same
//                         surface text (not a fresh human decision on *this* mention).

const SOURCE_CONFIG = {
    auto: {
        label: 'Auto',
        icon: SmartToyOutlinedIcon,
        color: 'default',
        variant: 'outlined',
    },
    'manual-annotation': {
        label: 'Manual',
        icon: PersonOutlinedIcon,
        color: 'info',
        variant: 'filled',
    },
    'manual-propagated': {
        label: 'Propagated',
        icon: ContentCopyOutlinedIcon,
        color: 'secondary',
        variant: 'outlined',
    },
};

function formatScore(v) {
    return v == null ? 'n/a' : `${Math.round(v * 100)}%`;
}

function SourceChip({source, linkedBy, nerScore, linkingScore}) {
    if (!source) return <Typography variant="caption" color="text.disabled">—</Typography>;
    const cfg = SOURCE_CONFIG[source] || SOURCE_CONFIG.auto;
    const Icon = cfg.icon;

    const chip = (
        <Chip
            icon={<Icon sx={{fontSize: '13px !important'}}/>}
            label={cfg.label}
            size="small"
            color={cfg.color}
            variant={cfg.variant}
            sx={{fontSize: 10, height: 22, fontWeight: 700}}
        />
    );

    let tooltip;
    if (source === 'manual-annotation') {
        tooltip = linkedBy ? `Linked manually by ${linkedBy}` : 'Linked manually';
    } else if (source === 'manual-propagated') {
        tooltip = linkedBy
            ? `Auto-applied from ${linkedBy}'s manual link elsewhere in this report (same surface text)`
            : 'Auto-applied from a manual link elsewhere in this report (same surface text)';
    } else {
        tooltip = `Linked automatically — GLiNER mention detection (${formatScore(nerScore)}) `
            + `+ ReFinED entity linking (${formatScore(linkingScore)}). No human review.`;
    }

    return <Tooltip title={tooltip} enterDelay={400}>{chip}</Tooltip>;
}

// ── NER / NEL score cell ──────────────────────────────────────────────────────
// Shows the two underlying confidence scores side by side: GLiNER mention-detection
// score (NER) and ReFinED entity-linking score (NEL). Blank for manual links/unlinked
// mentions, since those were never scored by the pipeline.

function scoreColor(v) {
    if (v == null) return 'text.disabled';
    if (v >= 0.9) return 'success.main';
    if (v >= 0.7) return 'warning.main';
    return 'error.main';
}

function ScoreBadge({label, value}) {
    return (
        <Tooltip
            title={value == null ? `${label} score not available` : `${label} confidence: ${formatScore(value)}`}
            enterDelay={500}
        >
            <Stack direction="row" spacing={0.4} alignItems="baseline" component="span">
                <Typography
                    variant="caption"
                    sx={{fontSize: 9, fontWeight: 700, color: 'text.disabled', letterSpacing: '0.03em'}}
                >
                    {label}
                </Typography>
                <Typography
                    variant="caption"
                    sx={{fontSize: 11, fontWeight: 700, fontFamily: 'monospace', color: scoreColor(value)}}
                >
                    {formatScore(value)}
                </Typography>
            </Stack>
        </Tooltip>
    );
}

function ScoreCell({nerScore, linkingScore}) {
    if (nerScore == null && linkingScore == null) {
        return <Typography variant="caption" color="text.disabled">—</Typography>;
    }
    return (
        <Stack direction="row" spacing={1.25}>
            <ScoreBadge label="NER" value={nerScore}/>
            <ScoreBadge label="NEL" value={linkingScore}/>
        </Stack>
    );
}

// ── Context preview (position-in-text) ───────────────────────────────────────
// Every mention row already carries its full surrounding text (contextText) plus the exact
// character span of the mention within it (mentionStart/mentionEnd) — preloaded with the page,
// no extra request needed. This renders a windowed snippet around that span with the mention
// highlighted, so hovering the Block ID immediately shows *where* the mention lives in the report.

const CONTEXT_WINDOW = 90; // chars of surrounding text to show on each side of the mention

function ContextPreview({contextText, mentionStart, mentionEnd}) {
    if (!contextText) {
        return <Typography variant="caption" sx={{color: 'grey.400'}}>No context available</Typography>;
    }
    if (mentionStart == null || mentionEnd == null) {
        // Position couldn't be resolved — still show the full context text (it's short/exact
        // for most rows), just without a highlight.
        return (
            <Typography variant="caption" component="div" sx={{color: 'grey.200', maxWidth: 380, lineHeight: 1.6}}>
                {contextText.length > 400 ? `${contextText.slice(0, 400)}…` : contextText}
            </Typography>
        );
    }
    const start = Math.max(0, mentionStart - CONTEXT_WINDOW);
    const end = Math.min(contextText.length, mentionEnd + CONTEXT_WINDOW);
    const before = (start > 0 ? '…' : '') + contextText.slice(start, mentionStart);
    const match = contextText.slice(mentionStart, mentionEnd);
    const after = contextText.slice(mentionEnd, end) + (end < contextText.length ? '…' : '');
    return (
        <Typography variant="caption" component="div" sx={{color: 'grey.200', maxWidth: 380, lineHeight: 1.7}}>
            {before}
            <Box
                component="mark"
                sx={{
                    bgcolor: 'warning.light',
                    color: 'warning.contrastText',
                    px: 0.4,
                    borderRadius: 0.5,
                    fontWeight: 700,
                }}
            >
                {match}
            </Box>
            {after}
        </Typography>
    );
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function ReportMentionsPage() {
    const {reportId} = useParams();

    const [activeTab, setActiveTab] = useState(0);
    const [search, setSearch] = useState('');
    const [debouncedQ, setDebouncedQ] = useState('');
    const [page, setPage] = useState(0);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [counts, setCounts] = useState(null);
    const [countsLoading, setCountsLoading] = useState(false);

    const searchTimer = useRef(null);

    const handleSearchChange = (v) => {
        setSearch(v);
        if (searchTimer.current) clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => {setDebouncedQ(v); setPage(0);}, 350);
    };

    const load = useCallback(() => {
        if (!reportId) return;
        setLoading(true);
        setError(null);
        const status = STATUS_TABS[activeTab].key;
        getReportMentions(reportId, {status, q: debouncedQ || undefined, page, size: PAGE_SIZE})
            .then(setData)
            .catch(e => setError(e.message || 'Failed to load mentions'))
            .finally(() => setLoading(false));
    }, [reportId, activeTab, debouncedQ, page]);

    useEffect(() => {load();}, [load]);

    // Preload tab badge counts up front (all 4 buckets in one call) — independent of which
    // tab is active, so switching tabs never shows a blank/stale badge. Re-fetched whenever
    // the search term changes so badges stay accurate while filtering.
    const loadCounts = useCallback(() => {
        if (!reportId) return;
        setCountsLoading(true);
        getReportMentionCounts(reportId, {q: debouncedQ || undefined})
            .then(setCounts)
            .catch(() => {/* non-fatal — tabs just show no badge */})
            .finally(() => setCountsLoading(false));
    }, [reportId, debouncedQ]);

    useEffect(() => {loadCounts();}, [loadCounts]);

    const totalPages = data ? Math.ceil(data.totalCount / PAGE_SIZE) : 0;
    const from = page * PAGE_SIZE + 1;
    const to = Math.min((page + 1) * PAGE_SIZE, data?.totalCount ?? 0);

    return (
        <Box sx={{flex: 1, display: 'flex', flexDirection: 'column', bgcolor: 'grey.50'}}>

            {/* ── Filter bar ── */}
            <Paper elevation={0} square sx={{borderBottom: '1px solid', borderColor: 'divider'}}>
                <Box sx={{maxWidth: 1280, mx: 'auto', px: 3}}>
                    <Stack direction="row" alignItems="flex-end" justifyContent="space-between" spacing={2}>
                        <Tabs
                            value={activeTab}
                            onChange={(_, v) => {setActiveTab(v); setPage(0);}}
                            textColor="primary"
                            indicatorColor="primary"
                            sx={{'& .MuiTab-root': {fontSize: 13, fontWeight: 600, minHeight: 48, textTransform: 'none', px: 2.5}}}
                        >
                            {STATUS_TABS.map((t, i) => {
                                const count = counts?.[t.countKey];
                                return (
                                    <Tab
                                        key={t.key}
                                        label={
                                            <Stack direction="row" alignItems="center" spacing={0.75}>
                                                <span>{t.label}</span>
                                                {count != null ? (
                                                    <Chip
                                                        label={count.toLocaleString()}
                                                        size="small"
                                                        color={activeTab === i ? 'primary' : 'default'}
                                                        sx={{height: 18, fontSize: 10, fontWeight: 700, ml: 0.5}}
                                                    />
                                                ) : countsLoading ? (
                                                    <Skeleton variant="rounded" width={22} height={16} sx={{ml: 0.5}}/>
                                                ) : null}
                                            </Stack>
                                        }
                                    />
                                );
                            })}
                        </Tabs>

                        <Box sx={{pb: 1}}>
                            <TextField
                                size="small"
                                value={search}
                                onChange={e => handleSearchChange(e.target.value)}
                                placeholder="Search mention or label…"
                                sx={{width: 260}}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            {loading
                                                ? <CircularProgress size={14} color="inherit"/>
                                                : <SearchIcon sx={{fontSize: 16, color: 'text.disabled'}}/>
                                            }
                                        </InputAdornment>
                                    ),
                                    sx: {fontSize: 13},
                                }}
                            />
                        </Box>
                    </Stack>
                </Box>
            </Paper>

            {/* ── Content ── */}
            <Box sx={{flex: 1, maxWidth: 1280, width: '100%', mx: 'auto', px: 3, py: 3}}>

                {/* Error state */}
                {error && (
                    <Paper
                        variant="outlined"
                        sx={{p: 3, borderColor: 'error.light', bgcolor: 'error.50', mb: 2}}
                    >
                        <Typography color="error" variant="body2">{error}</Typography>
                    </Paper>
                )}

                {/* Table */}
                {!error && (
                    <TableContainer
                        component={Paper}
                        variant="outlined"
                        sx={{borderRadius: 2, borderColor: 'divider', overflow: 'hidden'}}
                    >
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    {['Mention Text', 'Entity Label', 'ID', 'Source', 'NER / NEL Score', 'Status', 'Position', 'Block ID'].map(col => (
                                        <TableCell
                                            key={col}
                                            sx={{
                                                fontSize: 11,
                                                fontWeight: 700,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.06em',
                                                color: 'text.secondary',
                                                bgcolor: 'grey.50',
                                                borderBottom: '2px solid',
                                                borderColor: 'divider',
                                                py: 1.25,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {col}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {/* Loading skeleton */}
                                {loading && !data && (
                                    Array.from({length: 8}).map((_, i) => (
                                        <TableRow key={i}>
                                            {[200, 260, 100, 100, 120, 90, 80, 140].map((w, j) => (
                                                <TableCell key={j} sx={{py: 1.5}}>
                                                    <Skeleton variant="text" width={w * 0.6 + Math.random() * w * 0.4} height={16}/>
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                )}

                                {/* Empty state */}
                                {!loading && data && data.mentions.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={8} sx={{py: 8, textAlign: 'center'}}>
                                            <Typography variant="body2" color="text.disabled">
                                                No mentions found{search ? ` for "${search}"` : ''}.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}

                                {/* Data rows — grouped by block, each block gets a subtle
                                    alternating background so it's immediately clear which mentions
                                    live in the same paragraph, and whether blocks are in reading
                                    order top-to-bottom (they are: sorted by chunk_index ASC). */}
                                {data && (() => {
                                    // Build a stable band-colour map in the order blocks first appear
                                    // on the current page. Used ONLY for alternating row colours —
                                    // NOT for the block number badge (see blockNum below).
                                    const blockOrder = [];
                                    data.mentions.forEach(r => {
                                        if (r.blockId && !blockOrder.includes(r.blockId)) blockOrder.push(r.blockId);
                                    });
                                    // 6-shade palette — alternates across blocks, never looks like
                                    // a status colour. Chosen to be visible but not distracting.
                                    const BLOCK_BANDS = [
                                        'rgba(99,  102, 241, 0.06)',  // indigo
                                        'rgba(20,  184, 166, 0.07)',  // teal
                                        'rgba(245, 158,  11, 0.07)',  // amber
                                        'rgba(236,  72,  153, 0.06)', // pink
                                        'rgba(16,  185, 129, 0.07)',  // emerald
                                        'rgba(139,  92, 246, 0.06)',  // violet
                                    ];
                                    return data.mentions.map((row, i) => {
                                        const bandIdx = blockOrder.indexOf(row.blockId);
                                        // Stable document block number supplied directly by the
                                        // backend (computed from the full document, not the filtered
                                        // result set) — immune to search filters and pagination.
                                        const blockNum = row.blockOrder ?? null;
                                        const rowBg = bandIdx >= 0
                                            ? BLOCK_BANDS[bandIdx % BLOCK_BANDS.length]
                                            : 'transparent';
                                        // Draw a subtle top border only at block boundaries
                                        const isFirstInBlock = i === 0 || data.mentions[i - 1]?.blockId !== row.blockId;
                                        return (
                                    <TableRow
                                        key={i}
                                        hover
                                        sx={{
                                            opacity: loading ? 0.5 : 1,
                                            transition: 'opacity 0.15s',
                                            bgcolor: rowBg,
                                            '&:last-child td': {border: 0},
                                            ...(isFirstInBlock && i > 0 && {
                                                '& td': {borderTop: '2px solid', borderTopColor: 'divider'},
                                            }),
                                        }}
                                    >
                                        <TableCell sx={{maxWidth: 240, py: 1.25}}>
                                            <Stack direction="row" alignItems="center" spacing={0.75}>
                                                {/* Block number badge — shows reading order position (1-based) so you can
                                                    navigate top-to-bottom without scrolling to the Block ID column */}
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        minWidth: 22,
                                                        height: 18,
                                                        px: 0.6,
                                                        borderRadius: 0.75,
                                                        bgcolor: bandIdx >= 0
                                                            ? BLOCK_BANDS[bandIdx % BLOCK_BANDS.length].replace(/[\d.]+\)$/, '0.28)')
                                                            : 'grey.200',
                                                        fontSize: 10,
                                                        fontWeight: 800,
                                                        color: 'text.secondary',
                                                        letterSpacing: '0.02em',
                                                        flexShrink: 0,
                                                        fontFamily: 'monospace',
                                                    }}
                                                >
                                                    {blockNum != null ? `#${blockNum}` : '—'}
                                                </Box>
                                                <Tooltip title={row.mentionText || ''} enterDelay={600}>
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight={600}
                                                        sx={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 185}}
                                                    >
                                                        {row.mentionText || <span style={{color: '#bbb'}}>—</span>}
                                                    </Typography>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                        <TableCell sx={{maxWidth: 280, py: 1.25}}>
                                            <Tooltip title={row.entityLabel || ''} enterDelay={600}>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 270}}
                                                >
                                                    {row.entityLabel || <span style={{color: '#bbb'}}>—</span>}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={{py: 1.25}}>
                                            <IdChip wikidataId={row.wikidataId}/>
                                        </TableCell>
                                        <TableCell sx={{py: 1.25}}>
                                            <SourceChip
                                                source={row.linkSource}
                                                linkedBy={row.linkedBy}
                                                nerScore={row.nerScore}
                                                linkingScore={row.linkingScore}
                                            />
                                        </TableCell>
                                        <TableCell sx={{py: 1.25}}>
                                            <ScoreCell nerScore={row.nerScore} linkingScore={row.linkingScore}/>
                                        </TableCell>
                                        <TableCell sx={{py: 1.25}}>
                                            <StatusChip status={row.status}/>
                                        </TableCell>
                                        <TableCell sx={{py: 1.25, whiteSpace: 'nowrap'}}>
                                            {row.mentionStart != null && row.mentionEnd != null ? (
                                                <Typography variant="caption" sx={{fontFamily: 'monospace', fontSize: 11, color: 'text.secondary'}}>
                                                    {row.mentionStart}–{row.mentionEnd}
                                                </Typography>
                                            ) : (
                                                <Typography variant="caption" sx={{color: 'text.disabled'}}>—</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{maxWidth: 100, py: 1.25}}>
                                            <Tooltip
                                                title={
                                                    <ContextPreview
                                                        contextText={row.contextText}
                                                        mentionStart={row.mentionStart}
                                                        mentionEnd={row.mentionEnd}
                                                    />
                                                }
                                                enterDelay={300}
                                                placement="left"
                                                slotProps={{tooltip: {sx: {maxWidth: 420, p: 1.25}}}}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        fontFamily: 'monospace',
                                                        fontSize: 10,
                                                        color: 'text.disabled',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        display: 'block',
                                                        maxWidth: 80,
                                                        cursor: row.contextText ? 'help' : 'default',
                                                        textDecoration: row.contextText ? 'underline dotted' : 'none',
                                                        textDecorationColor: 'divider',
                                                    }}
                                                >
                                                    {row.blockId ? row.blockId.slice(0, 8) : '—'}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                        );
                                    });
                                })()}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {/* ── Pagination ── */}
                {data && totalPages > 1 && (
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mt: 2.5}}>
                        <Typography variant="caption" color="text.secondary">
                            Showing {from}–{to} of{' '}
                            <Box component="span" fontWeight={700} color="text.primary">
                                {data.totalCount.toLocaleString()}
                            </Box>
                        </Typography>

                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            <IconButton
                                size="small"
                                disabled={page === 0}
                                onClick={() => setPage(p => Math.max(0, p - 1))}
                                sx={{border: '1px solid', borderColor: 'divider', borderRadius: 1.5, bgcolor: 'background.paper'}}
                            >
                                <ChevronLeftIcon fontSize="small"/>
                            </IconButton>

                            <Typography variant="caption" color="text.secondary" sx={{px: 1.5}}>
                                {page + 1} / {totalPages}
                            </Typography>

                            <IconButton
                                size="small"
                                disabled={page >= totalPages - 1}
                                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                                sx={{border: '1px solid', borderColor: 'divider', borderRadius: 1.5, bgcolor: 'background.paper'}}
                            >
                                <ChevronRightIcon fontSize="small"/>
                            </IconButton>
                        </Stack>
                    </Stack>
                )}
            </Box>
        </Box>
    );
}
